/**
 * Ripescaggio settimanale dei candidati scaduti (F-184), nella pagina della
 * wishlist: interruttore, giorno e ora, «Esegui ora», ed esito dell'ultima
 * esecuzione con il motivo di ogni scelta.
 *
 * L'interruttore salva da solo: un interruttore che non vale finché non si
 * preme un altro pulsante è il modo più rapido per credere acceso ciò che è
 * spento. Giorno, ora e fuso si salvano insieme, con «Salva orario».
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api-client';
import type { AssetDomain } from '@/lib/asset-domain';
import { formatDate, formatDateTime } from '@/lib/format';
import { Alert, Badge, Button, Card, Field, Select, Spinner, Switch } from '@/ui';
import { DomainBadge } from '@/ui/DomainBadge';
import { wishlistKeys } from './api';
import {
  isInFlight,
  useRevivalOverview,
  useRunRevivalNow,
  useUpdateRevivalSettings,
  type RevivalOverview,
  type RevivalPick,
  type RevivalRun,
  type RevivalTarget,
} from './revival-api';

const WEEKDAYS = ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica'];

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

const STATUS: Record<RevivalRun['status'], { label: string; tone: 'neutral' | 'positive' | 'negative' | 'accent' | 'warning' }> = {
  queued: { label: 'in coda', tone: 'warning' },
  running: { label: 'in corso', tone: 'warning' },
  completed: { label: 'completato', tone: 'positive' },
  failed: { label: 'fallito', tone: 'negative' },
};

function limitsOf(target: RevivalTarget): string {
  const parts = [
    target.maxExposureAmount ? `max ${target.maxExposureAmount} ${target.baseCurrency}` : null,
    target.maxPortfolioPercentage ? `max ${target.maxPortfolioPercentage}%` : null,
  ].filter(Boolean);
  return parts.length > 0 ? ` (${parts.join(', ')})` : '';
}

function Destinations({ overview }: { overview: RevivalOverview }) {
  return (
    <ul className="revival__targets">
      {(['stock', 'crypto'] as AssetDomain[]).map((domain) => {
        const target = overview.targets[domain];
        return (
          <li key={domain}>
            <DomainBadge domain={domain} />{' '}
            {target ? (
              <>
                → «{target.portfolioName}»{limitsOf(target)}
              </>
            ) : (
              <span className="text-muted">
                nessun portafoglio nel <Link to="/discovery/daily-pick">titolo del giorno</Link>: i candidati{' '}
                {domain === 'stock' ? 'di borsa' : 'crypto'} restano fuori
                {overview.withoutPortfolio[domain] > 0 ? ` (oggi ${overview.withoutPortfolio[domain]})` : ''}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function PickRow({ pick }: { pick: RevivalPick }) {
  const outcome =
    pick.status === 'revived'
      ? `rimesso in wishlist su «${pick.portfolioName}» fino al ${formatDate(pick.expiresAt)}`
      : pick.status === 'skipped'
        ? `scelto ma non rimesso in wishlist: ${pick.note ?? 'motivo non registrato'}`
        : `scelta scartata: ${pick.note ?? 'motivo non registrato'}`;
  return (
    <li className="revival__pick" data-status={pick.status}>
      <div className="revival__pick-head">
        <span className="wishlist__name">{pick.name}</span>
        {pick.symbol && pick.symbol !== pick.name && <span className="text-muted">{pick.symbol}</span>}
        {pick.domain && <DomainBadge domain={pick.domain} />}
        <Badge tone={pick.status === 'revived' ? 'positive' : 'neutral'}>confidenza {pick.confidence}/100</Badge>
      </div>
      <div className="revival__pick-outcome">{outcome}</div>
      <div className="text-muted">{pick.reason}</div>
    </li>
  );
}

function LatestRun({ run }: { run: RevivalRun }) {
  const status = STATUS[run.status];
  return (
    <div className="revival__run">
      <div className="revival__run-head">
        <strong>Ultima esecuzione</strong>
        <Badge tone={status.tone}>{status.label}</Badge>
        <span className="text-muted">
          {run.trigger === 'scheduled' ? 'settimanale' : 'a mano'} · {formatDateTime(run.startedAt ?? run.createdAt)} ·{' '}
          {run.candidatesCount} candidati esaminati
        </span>
        {run.agentRunId && <Link to={`/agent-runs/${run.agentRunId}`}>Apri lo studio</Link>}
      </div>
      {isInFlight(run) && <Spinner label="Lo studio sta rileggendo i candidati scaduti…" />}
      {run.status === 'failed' && (
        <Alert variant="error" title="Ripescaggio non riuscito">
          {run.errorMessage}
        </Alert>
      )}
      {run.summary && <p>{run.summary}</p>}
      {run.emptyReason && <p className="text-muted">{run.emptyReason}</p>}
      {run.picks.length > 0 && (
        <ul className="revival__picks">
          {run.picks.map((pick) => (
            <PickRow key={`${pick.wishlistItemId}-${pick.status}`} pick={pick} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ScheduleForm({ overview }: { overview: RevivalOverview }) {
  const save = useUpdateRevivalSettings();
  const [form, setForm] = useState(overview.settings);
  // Le impostazioni tornano dal server dopo il salvataggio: il form le rilegge.
  useEffect(() => setForm(overview.settings), [overview.settings]);

  return (
    <div className="revival__schedule">
      <Select
        label="Giorno"
        value={String(form.weekday)}
        onChange={(event) => setForm({ ...form, weekday: Number(event.target.value) })}
        options={WEEKDAYS.map((label, index) => ({ value: String(index + 1), label }))}
      />
      <Field
        label="Ora"
        type="time"
        value={form.runAtLocalTime}
        onChange={(event) => setForm({ ...form, runAtLocalTime: event.target.value })}
      />
      <Field
        label="Fuso orario"
        value={form.timezone}
        onChange={(event) => setForm({ ...form, timezone: event.target.value })}
        hint="Nome IANA, es. Europe/Rome."
      />
      <Button
        variant="secondary"
        onClick={() => save.mutate({ weekday: form.weekday, runAtLocalTime: form.runAtLocalTime, timezone: form.timezone })}
        disabled={save.isPending}
      >
        {save.isPending ? 'Salvo…' : 'Salva orario'}
      </Button>
      {save.isError && (
        <Alert variant="error" title="Non ho potuto salvare">
          {errorMessage(save.error)}
        </Alert>
      )}
    </div>
  );
}

export function WishlistRevivalCard() {
  const overview = useRevivalOverview();
  const toggle = useUpdateRevivalSettings();
  const runNow = useRunRevivalNow();
  const queryClient = useQueryClient();

  const latest = overview.data?.runs[0];
  // Quando uno studio finisce, la tabella sotto può avere righe nuove.
  const previousStatus = useRef(latest?.status);
  useEffect(() => {
    if (isInFlight({ status: previousStatus.current }) && latest && !isInFlight(latest)) {
      void queryClient.invalidateQueries({ queryKey: wishlistKeys.all });
    }
    previousStatus.current = latest?.status;
  }, [latest, queryClient]);

  if (overview.isLoading) return <Spinner label="Caricamento del ripescaggio…" />;
  if (overview.isError || !overview.data) {
    return (
      <Alert variant="error" title="Ripescaggio non disponibile">
        {errorMessage(overview.error)}
      </Alert>
    );
  }

  const data = overview.data;
  const busy = isInFlight(latest) || runNow.isPending;

  return (
    <Card
      title="Ripescaggio dei candidati scaduti"
      subtitle={`Una volta alla settimana uno studio agentico rilegge i candidati scaduti negli ultimi ${data.lookbackDays} giorni e ne rimette in wishlist da 0 a ${data.maxPicks}, sul portafoglio e con i tetti del titolo del giorno, per ${data.defaultExpiryDays} giorni. Non compra e non vende.`}
      action={
        <Button
          variant="secondary"
          onClick={() => runNow.mutate()}
          disabled={!data.agentRuntimeEnabled || busy || data.eligibleCount === 0}
          title={data.eligibleCount === 0 ? 'Nessun candidato ripescabile adesso' : undefined}
        >
          {busy ? 'In corso…' : 'Esegui ora'}
        </Button>
      }
    >
      <div className="revival">
        {!data.agentRuntimeEnabled && (
          <Alert variant="warning" title="Runtime agentico spento sul server">
            Il ripescaggio non può partire finché il runtime agentico non è attivo.
          </Alert>
        )}
        {(toggle.isError || runNow.isError) && (
          <Alert variant="error" title="Operazione non riuscita">
            {errorMessage(toggle.error ?? runNow.error)}
          </Alert>
        )}

        <Switch
          label="Ripesca automaticamente ogni settimana"
          checked={data.settings.enabled}
          onChange={(enabled) => toggle.mutate({ enabled })}
          disabled={toggle.isPending}
        />
        <p className="text-muted">
          {data.settings.enabled && data.nextRunAt
            ? `Prossima esecuzione: ${WEEKDAYS[data.settings.weekday - 1]} ${formatDateTime(data.nextRunAt)}.`
            : 'Spento: parte solo con «Esegui ora».'}{' '}
          Oggi andrebbero allo studio {data.eligibleCount} candidati.
        </p>

        <ScheduleForm overview={data} />
        <Destinations overview={data} />

        {latest && <LatestRun run={latest} />}
      </div>
    </Card>
  );
}
